import type {
  GameModeData,
  GameModeRequestData,
  GameModesQueryParams
} from '@library/types/gameModes';
import {
  mapGameModeDataToRequest as mapGameModeDataToRequestUtil,
  transformGameModeData
} from '@bo-utils/gameModes';
import {
  transformAllowedStateData,
  transformAllowedStateOptions
} from '@bo-utils/allowedStates';
import {
  transformGameTypeData,
  transformGameTypeOptions
} from '@bo-utils/gameTypes';
import { useCallback, useMemo } from 'react';
import {
  useCreateGameModeDashMutation,
  useGetGameModesDashQuery,
  useUpdateGameModeDashMutation
} from '@bo-services/gameModesDashApi';

import type { LeaguesList } from '@library/types/leagues';
import type { SelectOption } from '@library/types/utils';
import { useGetAllowedStatesQuery } from '@bo-services/allowedStatesApi';
import { useGetGameTypesQuery } from '@bo-services/gameTypesApi';

export const useGameModesDash = ({
  gameModesQueryParams,
  currentItemId,
  setSubmitMessage
}: {
  gameModesQueryParams: GameModesQueryParams;
  leaguesList: LeaguesList;
  currentItemId: string;
  setSubmitMessage: (message: string) => void;
}) => {
  const {
    data: response,
    isError,
    isLoading,
    isSuccess
  } = useGetGameModesDashQuery(gameModesQueryParams);

  const { data: gameTypes } = useGetGameTypesQuery({
    page: 1,
    perPage: '100',
    sortBy: 'id',
    sortDir: 'asc'
  });

  const gameTypesData = useMemo(
    () => transformGameTypeData(gameTypes),
    [gameTypes]
  );

  const { data: allowedStates } = useGetAllowedStatesQuery({
    page: 1,
    perPage: '100',
    sortBy: 'id',
    sortDir: 'asc'
  });

  const allowedStatesData = useMemo(
    () => transformAllowedStateData(allowedStates),
    [allowedStates]
  );

  const [createGameMode] = useCreateGameModeDashMutation();
  const [updateGameMode] = useUpdateGameModeDashMutation();

  const gameModesData = useMemo(
    () =>
      transformGameModeData(response, {
        gameTypes: gameTypesData,
        allowedStates: allowedStatesData
      }),
    [response, gameTypesData, allowedStatesData]
  );

  const mapGameModeDataToRequest = useCallback(
    (gameModeFormData: Partial<GameModeData>): GameModeRequestData =>
      mapGameModeDataToRequestUtil(gameModeFormData),
    []
  );

  const handleUpdate = useCallback(
    async (formData: Partial<GameModeData>) => {
      await updateGameMode({
        id: currentItemId,
        data: mapGameModeDataToRequest(formData)
      })
        .unwrap()
        .then((payload) => {
          // eslint-disable-next-line no-console
          console.log('fulfilled', payload);
          if (!payload.data) throw new Error('Permission denied');
          setSubmitMessage('Game Mode updated');
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('rejected', error);
          setSubmitMessage(`Couldn't update Game Mode - ${error}`);
        });
    },
    [updateGameMode, currentItemId, mapGameModeDataToRequest, setSubmitMessage]
  );

  const handleCreate = useCallback(
    async (formData: Partial<GameModeData>) => {
      await createGameMode(mapGameModeDataToRequest(formData))
        .unwrap()
        .then((payload) => {
          // eslint-disable-next-line no-console
          console.log('fulfilled', payload);
          if (!payload.data) throw new Error('Permission denied');
          setSubmitMessage('Game Mode created');
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('rejected', error);
          setSubmitMessage(`Couldn't create Game Mode - ${error}`);
        });
    },
    [createGameMode, mapGameModeDataToRequest, setSubmitMessage]
  );

  const gameTypesOptions: Array<SelectOption> = useMemo(() => {
    return transformGameTypeOptions(gameTypesData);
  }, [gameTypesData]);

  const allowedStatesOptions: Array<SelectOption> = useMemo(() => {
    return transformAllowedStateOptions(allowedStatesData);
  }, [allowedStatesData]);

  return {
    createGameMode: handleCreate,
    isError,
    isLoading,
    isSuccess,
    mapGameModeDataToRequest,
    meta: response?.meta || {},
    page: response?.meta?.current_page || 0,
    gameModes: gameModesData || [],
    totalPages: response?.meta?.total_pages || 0,
    updateGameMode: handleUpdate,
    gameTypeOptions: gameTypesOptions || [],
    allowedStatesOptions: allowedStatesOptions || [],
    relationships: {
      gameTypes: gameTypesData,
      allowedStates: allowedStatesData
    }
  };
};
